import React, {useEffect, useRef, useState} from 'react'
import moment from 'moment'
import {Spinner} from 'react-bootstrap'
import toaster from '../../../../Utils/toaster'
import {AppointmentStatusBadge} from '../../../../components/AppointmentStatusBadge'
import {
  getIpiVisCriticalAppointments,
  type IpiVisCriticalAppointment,
  type IpiVisFilters,
} from './_requests'

const APP_ORIGIN = window.location.origin
const PAGE_SIZE = 6
const EMPTY_VALUE = '--'
const TITLE_ID = 'ipivis-critical-title'
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'

type Props = {
  filters: IpiVisFilters
  onClose: () => void
}

type PageItem = number | string

const buildPageItems = (currentPage: number, totalPages: number): PageItem[] => {
  const candidates = [1, currentPage - 1, currentPage, currentPage + 1, totalPages]
  const visible = Array.from(new Set(candidates))
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((a, b) => a - b)

  return visible.reduce<PageItem[]>((items, page, index) => {
    const previous = visible[index - 1]
    if (previous !== undefined && page - previous === 2) items.push(previous + 1)
    if (previous !== undefined && page - previous > 2) items.push(`ellipsis-${page}`)
    items.push(page)
    return items
  }, [])
}

export const CriticalAppointmentsModal: React.FC<Props> = ({filters, onClose}) => {
  const [rows, setRows] = useState<IpiVisCriticalAppointment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const panelRef = useRef<HTMLDivElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    let cancelled = false
    const loadAppointments = async () => {
      try {
        const result = await getIpiVisCriticalAppointments(filters)
        if (!cancelled) {
          setRows(result)
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(true)
          toaster('error', e?.response?.data?.text || 'Failed to load critical appointments.')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }
    loadAppointments()
    return () => {
      cancelled = true
    }
  }, [filters])

  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null
    closeButtonRef.current?.focus()

    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !panelRef.current) return

      const focusable = panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const focusIsInside = panelRef.current.contains(document.activeElement)

      if (event.shiftKey && (document.activeElement === first || !focusIsInside)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (document.activeElement === last || !focusIsInside)) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', trapFocus)
    return () => {
      document.removeEventListener('keydown', trapFocus)
      previouslyFocused?.focus()
    }
  }, [])

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', closeOnEscape)

    return () => {
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [onClose])

  useEffect(() => {
    const focused = document.activeElement as HTMLButtonElement | null
    if (focused === document.body || focused?.disabled) {
      panelRef.current?.querySelector<HTMLElement>('[aria-current="page"]')?.focus()
    }
  }, [currentPage])

  const totalPages = Math.ceil(rows.length / PAGE_SIZE)
  const pageRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  return (
    <div className='ipivis-note-overlay' onClick={onClose}>
      <div
        ref={panelRef}
        className='ipivis-note-panel ipivis-note-panel--critical'
        role='dialog'
        aria-modal='true'
        aria-labelledby={TITLE_ID}
        onClick={(e) => e.stopPropagation()}
      >
        <div className='ipivis-note-panel__header'>
          <h3 id={TITLE_ID} className='ipivis-note-panel__title'>
            Critical Appointments
          </h3>
          <button
            ref={closeButtonRef}
            className='ipivis-note-panel__close'
            onClick={onClose}
            type='button'
            aria-label='Close'
          >
            ×
          </button>
        </div>

        <div className='ipivis-note-panel__body'>
          {loading ? (
            <div className='ipivis-critical__loading'>
              <Spinner animation='border' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </Spinner>
            </div>
          ) : error ? (
            <div className='ipivis-empty'>Failed to load critical appointments.</div>
          ) : rows.length === 0 ? (
            <div className='ipivis-empty'>No critical open appointments in the next 24 hours.</div>
          ) : (
            <div className='ipivis-critical__table-wrapper'>
              <table className='ipivis-critical__table'>
                <thead>
                  <tr>
                    <th scope='col'>ID</th>
                    <th scope='col'>Recurring ID</th>
                    <th scope='col'>Customer Name</th>
                    <th scope='col'>Date</th>
                    <th scope='col'>Time</th>
                    <th scope='col'>Language</th>
                    <th scope='col'>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row) => (
                    <tr key={row.code}>
                      <td>
                        <a
                          className='ipivis-critical__id'
                          href={`${APP_ORIGIN}/appointmentViewer/${row.code}`}
                          target='_blank'
                          rel='noopener noreferrer'
                        >
                          #{row.code}
                        </a>
                      </td>
                      <td>{row.recurringId ?? EMPTY_VALUE}</td>
                      <td>{row.customerName ?? EMPTY_VALUE}</td>
                      <td>{moment(row.startTime).format('MMM DD, YYYY')}</td>
                      <td>{moment(row.startTime).format('hh:mm A')}</td>
                      <td>{row.languageTo ?? EMPTY_VALUE}</td>
                      <td>
                        <AppointmentStatusBadge status={row.status} className='fs-8 py-2 px-3' />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className='ipivis-critical__footer'>
          {totalPages > 1 && (
            <nav aria-label='Critical appointments pages'>
              <ul className='pagination ipivis-critical__pages'>
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    type='button'
                    className='page-link'
                    aria-label='Previous page'
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(currentPage - 1)}
                  >
                    <i className='bi bi-chevron-left' />
                  </button>
                </li>
                {buildPageItems(currentPage, totalPages).map((item) =>
                  typeof item === 'number' ? (
                    <li key={item} className={`page-item ${item === currentPage ? 'active' : ''}`}>
                      <button
                        type='button'
                        className='page-link'
                        aria-current={item === currentPage ? 'page' : undefined}
                        onClick={() => setCurrentPage(item)}
                      >
                        {item}
                      </button>
                    </li>
                  ) : (
                    <li key={item} className='page-item disabled'>
                      <span className='page-link'>…</span>
                    </li>
                  )
                )}
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    type='button'
                    className='page-link'
                    aria-label='Next page'
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(currentPage + 1)}
                  >
                    <i className='bi bi-chevron-right' />
                  </button>
                </li>
              </ul>
            </nav>
          )}

          <div className='ipivis-critical__actions'>
            <button type='button' className='btn btn-light me-3' onClick={onClose}>
              Cancel
            </button>
            <button type='button' className='btn btn-primary' onClick={onClose}>
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
